import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@advanza/shadcn-ui/src/components/ui/table'
import { Icon } from '@advanza/ui'
import clsx from 'clsx'
import Avatar from 'components/adminUsers/Avatar'
import auroraStyle from 'css/aurora.module.css'
import ScoringBreakdown from './ScoringBreakdown'
import type { ProviderResult, ScoringMode, SearchResponse } from './types'

interface ResultsTableProps {
    results: SearchResponse
    timer: number
    isLoading?: boolean
    mode: ScoringMode
    cityUrl?: string
}

const ResultsTable = ({ results, timer, isLoading, mode, cityUrl }: ResultsTableProps) => {
    const providers = results.providers || []

    return (
        <div className="relative space-y-3">
            {isLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60 backdrop-blur-[1px]">
                    <img
                        src="https://static.trustoo.nl/misc/claude-vibe-blob.svg"
                        alt="Loading"
                        className="h-24 w-24 animate-pulse"
                    />
                </div>
            )}
            <div className="flex items-center gap-4 text-md text-foreground">
                <span>
                    <b>{results.count}</b> results
                </span>
                <span>{(timer / 1000).toFixed(2)}s</span>
                {results.shouldDoSecondSort && (
                    <span className="flex items-center gap-1 text-tertiary-500">
                        <Icon name="sort" fontSize={14} />
                        Second sort applied
                    </span>
                )}
                {results.notEnoughLocalPros && (
                    <span className="flex items-center gap-1 text-tertiary-500">
                        <Icon name="rule" fontSize={14} />
                        Not enough local pros
                    </span>
                )}
                {(() => {
                    const extraCount = providers.filter((p) => p._isExtra).length
                    return extraCount > 0 ? (
                        <span className="flex items-center gap-1 text-secondary">
                            <Icon name="add" fontSize={14} />
                            {extraCount} extra locals
                        </span>
                    ) : null
                })()}
            </div>

            <div className="overflow-x-auto rounded-lg border border-grey-200 bg-white">
                <Table>
                    <TableHeader>
                        <TableRow className="text-sm">
                            <TableHead className="w-10">#</TableHead>
                            <TableHead className="w-8"></TableHead>
                            <TableHead>Provider</TableHead>
                            <TableHead className="w-20">Status</TableHead>
                            <TableHead className="w-12 text-center">Local</TableHead>
                            <TableHead className="w-16 text-right">Score</TableHead>
                            <TableHead className="w-20 text-right">Reclaim</TableHead>
                            <TableHead className="w-20">Response</TableHead>
                            <TableHead className="w-16 text-right">Dist.</TableHead>
                            <TableHead className="w-20 text-right">ES Score</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {providers.map((provider, i) => (
                            <ProviderRow
                                key={provider.service_provider_id}
                                provider={provider}
                                rank={i + 1}
                                mode={mode}
                                cityUrl={cityUrl}
                            />
                        ))}
                        {providers.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={10}
                                    className="py-8 text-center text-muted-foreground">
                                    No results
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}

interface ProviderRowProps {
    provider: ProviderResult
    rank: number
    mode: ScoringMode
    cityUrl?: string
}

const ProviderRow = ({ provider, rank, mode, cityUrl }: ProviderRowProps) => {
    const hasLeadBooster = provider.hasActiveLeadBooster || provider.lead_booster_active

    return (
        <TableRow className={clsx('text-sm', hasLeadBooster && auroraStyle.aurora)}>
            <TableCell className="font-bold text-foreground">{rank}</TableCell>
            <TableCell>
                {/* @ts-expect-error — JS component with loose prop types */}
                <Avatar sizePx={22} useLetterColor user={provider.user} />
            </TableCell>
            <TableCell>
                <div className="flex items-center gap-1">
                    <a
                        href={`/service/providers/provider/${provider.service_provider_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline">
                        {provider.business_name}
                    </a>
                    {provider.isAdvertiser && (
                        <img
                            src="https://static.trustoo.nl/icon/premium.svg"
                            width="14"
                            alt="Premium"
                            title="Premium advertiser"
                            className="cursor-help opacity-80 hover:opacity-100"
                        />
                    )}
                    {provider.claimed && (
                        <img
                            src="https://static.trustoo.nl/icon/claimed.svg"
                            width="14"
                            alt="Claimed"
                            title="Claimed profile"
                            className="cursor-help opacity-80 hover:opacity-100"
                        />
                    )}
                    {hasLeadBooster && (
                        <span
                            className="cursor-help opacity-80 hover:opacity-100"
                            title="Lead booster active">
                            <Icon name="rocket_launch" fontSize={13} />
                        </span>
                    )}
                </div>
            </TableCell>
            <TableCell>
                <ProviderStatus provider={provider} />
            </TableCell>
            <TableCell className="text-center">
                {provider.isLocal && (
                    <span className="cursor-help opacity-80 hover:opacity-100">
                        <Icon name="near_me" fontSize={16} title="Local provider" />
                    </span>
                )}
                {(provider._isExtra || provider.is_extra_local) && (
                    <span className="text-secondary text-[10px]">+local</span>
                )}
            </TableCell>
            <TableCell className="text-right font-mono font-bold">
                {(provider.total_score * 10).toFixed(1)}
            </TableCell>
            <TableCell className="text-right font-mono">
                {((1 - provider.reclaim_factor) * 100).toFixed(1)}%
            </TableCell>
            <TableCell>
                <ResponseBadge label={provider.response_label} />
            </TableCell>
            <TableCell className="text-right font-mono">
                {provider.distance === undefined
                    ? '-'
                    : `${parseFloat(String(provider.distance)).toFixed(0)}km`}
            </TableCell>
            <TableCell className="text-right">
                <ScoringBreakdown provider={provider} mode={mode} cityUrl={cityUrl} />
            </TableCell>
        </TableRow>
    )
}

interface ProviderStatusProps {
    provider: ProviderResult
}

const ProviderStatus = ({ provider }: ProviderStatusProps) => {
    const {
        is_paused,
        limits_status,
        active_status,
        has_active_presence_subscription,
        free_trial_status,
    } = provider

    if (is_paused && has_active_presence_subscription) {
        return (
            <span
                className="flex items-center gap-1 text-amber-600"
                title="Paused with presence subscription">
                <Icon name="visibility" fontSize={14} />
                <span className="text-[10px]">presence</span>
            </span>
        )
    }
    if (is_paused) {
        return (
            <span className="flex items-center gap-1 text-amber-500" title="Paused">
                <Icon name="pause" fontSize={14} />
                <span className="text-[10px]">paused</span>
            </span>
        )
    }
    if (limits_status === 'limit_reached') {
        return (
            <span className="flex items-center gap-1 text-amber-500" title="Week limit reached">
                <Icon name="block" fontSize={14} />
                <span className="text-[10px]">limit</span>
            </span>
        )
    }
    if (limits_status === 'limit_reached_partial') {
        return (
            <span className="flex items-center gap-1 text-amber-400" title="Partial limit reached">
                <Icon name="timelapse" fontSize={14} />
                <span className="text-[10px]">partial</span>
            </span>
        )
    }
    if (limits_status === 'free_limit_reached') {
        return (
            <span className="flex items-center gap-1 text-amber-500" title="Free limit reached">
                <Icon name="block" fontSize={14} />
                <span className="text-[10px]">free limit</span>
            </span>
        )
    }
    if (limits_status !== 'available') {
        return (
            <span
                className="flex items-center gap-1 text-amber-500"
                title={`Limits: ${limits_status}`}>
                <Icon name="block" fontSize={14} />
                <span className="text-[10px]">{limits_status}</span>
            </span>
        )
    }
    if (free_trial_status === 'free_trial_active') {
        return (
            <span className="flex items-center gap-1 text-blue-500" title="Free trial active">
                <Icon name="play_arrow" fontSize={14} />
                <span className="text-[10px]">trial</span>
            </span>
        )
    }
    if (active_status === 'active') {
        return (
            <span className="flex items-center gap-1 text-green-500" title="Active">
                <Icon name="play_arrow" fontSize={14} />
                <span className="text-[10px]">active</span>
            </span>
        )
    }
    return (
        <span
            className="flex items-center gap-1 text-grey-700"
            title={`${limits_status} ${active_status}`}>
            <Icon name="stop" fontSize={14} />
            <span className="text-[10px]">{active_status}</span>
        </span>
    )
}

interface ResponseBadgeProps {
    label: string
}

const ResponseBadge = ({ label }: ResponseBadgeProps) => {
    const colorMap: Record<string, string> = {
        good: 'bg-green-200 text-green-800',
        moderate: 'bg-yellow-100 text-yellow-800',
        poor: 'bg-red-100 text-red-800',
        no_response: 'bg-orange-100 text-orange-800',
    }
    return (
        <span
            className={clsx(
                'inline-block rounded px-1.5 py-0.5 text-[10px]',
                colorMap[label] || 'bg-grey-200 text-foreground',
            )}>
            {label === 'no_response' ? 'no response' : label}
        </span>
    )
}

export default ResultsTable
